import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DateValidation} from '../../validators/dateValidation'
import {localeSwedish} from '../locale.swedish';
import {ToastrService} from 'ngx-toastr';
import {Location} from '../../model/location';
import {TransferSummary, TransferSummaryReport} from '../../model/report/transferSummaryReport';
import * as moment from 'moment';
import {ISpinnerConfig, NgxSpinner, SPINNER_ANIMATIONS, SPINNER_PLACEMENT} from '@hardpool/ngx-spinner';
import {BaseChartDirective} from 'ng2-charts'
import {ExcelReportWriterHelper} from '../../helpers/excelReportWriterHelper';
import {LocationService} from '../../services/location.service';
import {ReportService} from '../../services/report.service';

@Component({
  selector: 'transferSummary',
  templateUrl: 'transferSummary.component.html',
  styleUrls: ['transferSummary.component.css'],
})
export class TransferSummaryComponent implements OnInit {

  @ViewChild(BaseChartDirective) myChart: any;
  @ViewChild("spinner", { static: true }) spinner: NgxSpinner;

  searchForm: UntypedFormGroup;
  busy: boolean = false;
  noMatch: boolean = true;
  performedSearch: boolean = false;

  maxDate: Date = new Date();

  locations: Location[];
  transferSummaryReport: TransferSummaryReport;

  fromControl: UntypedFormControl;
  toControl: UntypedFormControl;
  transferControl: UntypedFormControl;

  spinnerConfig: ISpinnerConfig;

  localeText = {};

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly toaster: ToastrService,
    private readonly locationService: LocationService,
    private readonly reportService: ReportService) {

    this.fromControl = new UntypedFormControl([Validators.required, DateValidation()]);
    this.toControl = new UntypedFormControl([Validators.required, DateValidation()]);
    this.transferControl = new UntypedFormControl();

    this.localeText = localeSwedish;

    this.searchForm = this.fb.group(
      {
        from: this.fromControl,
        to: this.toControl,
        transfer: this.transferControl
      });
  }

  ngOnInit() {
    this.spinnerConfig = {
      placement: SPINNER_PLACEMENT.block_ui,
      animation: SPINNER_ANIMATIONS.horizontal_bounce,
      size: "2rem",
      color: "#7AB800",
      bgColor: "transparent"
    };

    this.locations = this.locationService.Locations.filter(x => x.type == "T");
    let all = new Location(null);
    all.name = "Alla";
    this.locations.push(all);
    this.transferControl.setValue(all);
  }

  public lineChartData: Array<any> = [
    { data: [] = [], label: 'Utförda', hidden: false },
    { data: [] = [], label: 'BOM', hidden: false }
  ];

  lineChartLabels: string[] = [];

  lineChartOptions = {
    responsive: true,
    elements: { point: { radius: 5, hitRadius: 10, hoverRadius: 10 } }
  };

  lineChartLegend = true;
  lineChartPlugins = [];
  lineChartType = 'line';

  public lineChartColors = [
    { // Finished
      backgroundColor: 'transparent',
      borderColor: 'green'
    },
    { // NoShow
      backgroundColor: 'transparent',
      borderColor: 'red'
    },
  ];

  toMinValue() {
    return this.fromControl.value;
  }

  fromMaxValue() {
    return this.fromControl.dirty ? this.fromControl.value : this.maxDate;
  }

  canSearch() {
    return this.searchForm.valid && !this.busy;
  }

  async search() {
    if (this.searchForm.valid) {
      this.spinner.show();
      this.busy = true;

      let from = new Date(this.fromControl.value);
      let to = new Date(this.toControl.value);
      let transfer = this.transferControl.value;

      let dateKeys = [];
      let totalFinished: number[] = [];
      let totalNoShow: number[] = [];

      try {
        this.transferSummaryReport = await this.reportService.gettransferSummaryReport(from.toDateString(), to.toDateString(), transfer.name);
        this.transferSummaryReport.transferSummaries = this.sortReports(this.transferSummaryReport.transferSummaries);

        this.transferSummaryReport.transferSummaries.forEach((transferSummary: TransferSummary) => {
          dateKeys.push(transferSummary.dateKey);

          if (transferSummary.details !== undefined && transferSummary.details.length > 0) {
            totalFinished.push(transferSummary.details.map(d => d.finished).reduce((acc, val) => acc + val));
            totalNoShow.push(transferSummary.details.map(d => d.noShow).reduce((acc, val) => acc + val));
          }
        });

        this.lineChartLabels = [];
        for (let i = 0; i < dateKeys.length; i++) {
          let data = (i == 0 || i == dateKeys.length - 1)
          ? moment(dateKeys[i]).format("YYYY-MM-DD").toString()
          : "";
          this.lineChartLabels.push(data)
        }

        this.lineChartData[0].data = totalFinished;
        this.lineChartData[1].data = totalNoShow;

        this.noMatch = this.transferSummaryReport.transferSummaries.length == 0;
        this.performedSearch = true;
      }
      catch (error) {
        this.noMatch = error.request.status == 204;
        this.lineChartLabels = [];
        this.lineChartData[0].data = [];
        this.lineChartData[1].data = [];

        if (!this.noMatch)
          this.toaster.error("Ett fel uppstod vid inläsning av data.")
      }

      this.busy = false;
      this.spinner.hide();
    }
  }

  sortReports(input: any): any {
    return input.sort((a: TransferSummary, b: TransferSummary) => {
      let aStart = this.getTime(a.dateKey);
      let bStart = this.getTime(b.dateKey);
      if (aStart < bStart)
        return -1;
      else if (aStart > bStart)
        return 1;
      else
        return 0;
    });
  }

  getTime(dateKey: string) {
    return moment(dateKey).format('HH:mm');
  }

  exportSummary() {
    ExcelReportWriterHelper.exportTransferSummary(
      this.transferSummaryReport,
      this.transferControl.value.name,
      this.fromControl.value,
      this.toControl.value,
      this.myChart.chart.legend.legendItems[0].hidden,
      this.myChart.chart.legend.legendItems[1].hidden)
  }

  canExport() {
    return this.transferSummaryReport && this.transferSummaryReport.transferSummaries.length > 0 && !this.busy;
  }
}
